import React, { useRef, useEffect, useState } from 'react';
import Viewer from 'react-viewer';
import type { ImageDecorator } from 'react-viewer/lib/ViewerProps';

type Props = {
  imageUrls: (string | { src: string, alt: string })[],
  domId: string
}

// TypeScript 4.0's DOM definitions predate ResizeObserver; the browser API is
// optional and does not require a polyfill or a dependency upgrade.
type ContainerObserver = {
  observe: (element: Element) => void,
  disconnect: () => void
}
type ContainerObserverConstructor = new (
  callback: (entries: { contentRect: { width: number, height: number } }[]) => void
) => ContainerObserver;

const toolLabels: { [key: string]: string } = {
  zoomIn: 'Увеличить',
  zoomOut: 'Уменьшить',
  reset: 'Исходный масштаб',
  rotateLeft: 'Повернуть влево',
  rotateRight: 'Повернуть вправо',
  download: 'Скачать документ',
  prev: 'Предыдущее изображение',
  next: 'Следующее изображение',
  scaleX: 'Отразить по горизонтали',
  scaleY: 'Отразить по вертикали',
};

const ImageViewer: React.FC<Props> = ({ imageUrls, domId }) => {
  const container = useRef(document.getElementById(domId));
  const activeIndex = useRef(0);
  const sourceKey = JSON.stringify(imageUrls);
  const [prepared, setPrepared] = useState<{ key: string, images: ImageDecorator[], initialIndex: number } | null>(null);

  useEffect(() => {
    // Keep the descriptors stable while viewing. react-viewer reloads/reset its
    // internal transform whenever images changes, even if every src is unchanged.
    // A lazy defaultSize is read by the library only when loading an image or
    // explicitly resetting it, so Reset can fit the current container without
    // discarding the operator's zoom/rotation on resize or another image's load.
    const sources: Props['imageUrls'] = JSON.parse(sourceKey);
    const images = sources.map(source => {
      const item = typeof source === 'string' ? { src: source } : source;
      const probe = new Image();
      // Start before the Viewer mounts its own loader. The shared resource has
      // its natural dimensions by the time that loader reads defaultSize.
      probe.src = item.src;
      return {
        ...item,
        downloadUrl: item.src,
        get defaultSize() {
          if (!container.current || !probe.naturalWidth || !probe.naturalHeight) return undefined;
          const width = Math.max(1, container.current.offsetWidth - 32);
          const height = Math.max(1, container.current.offsetHeight - 104);
          const scale = window.innerWidth <= 900 ? width / probe.naturalWidth
            : Math.min(width / probe.naturalWidth, height / probe.naturalHeight);
          return { width: probe.naturalWidth * scale, height: probe.naturalHeight * scale };
        },
      };
    });
    // Keep this prop fixed for the mounted list. Sending its updated internal
    // selection back on an unrelated parent render also triggers a vendor reset.
    setPrepared({ key: sourceKey, images, initialIndex: Math.min(activeIndex.current, Math.max(0, images.length - 1)) });
  }, [sourceKey]);

  useEffect(() => {
    const Observer = (window as Window & { ResizeObserver?: ContainerObserverConstructor }).ResizeObserver;
    if (!container.current) return;
    const element = container.current;
    let containerHeight = element.offsetHeight;
    const positionImage = () => {
      const image = element.querySelector<HTMLImageElement>('.react-viewer-canvas img');
      const imageHeight = image ? parseFloat(image.style.height) : 0;
      // Use the library's actual base height, not a newly calculated fit. Its
      // transforms (including zoom, rotation and dragging) remain library-owned.
      const offset = imageHeight ? 16 - (containerHeight - imageHeight - 84) / 2 : 0;
      const value = `${offset}px`;
      if (element.style.getPropertyValue('--sg-viewer-image-offset') !== value)
        element.style.setProperty('--sg-viewer-image-offset', value);
    };
    const measure = () => {
      containerHeight = element.offsetHeight;
      positionImage();
    };
    measure();
    const observer = Observer ? new Observer(measure) : null;
    if (observer) observer.observe(element);
    const imageObserver = new MutationObserver(positionImage);
    imageObserver.observe(element, { subtree: true, childList: true, attributes: true, attributeFilter: ['style'] });
    window.addEventListener('resize', measure);
    return () => {
      if (observer) observer.disconnect();
      imageObserver.disconnect();
      window.removeEventListener('resize', measure);
      element.style.removeProperty('--sg-viewer-image-offset');
    };
  }, []);

  // Mount with the final descriptors once. Updating them during cached-image
  // completion can strand react-viewer 3.2's startLoading flag. Only a genuinely
  // different image list remounts the presentation; dimensions and themes do not.
  if (!prepared || prepared.key !== sourceKey) return null;
  const images = prepared.images;

  return (
      <Viewer
        key={sourceKey}
        container={container.current}
        activeIndex={prepared.initialIndex}
        onChange={(_image, index) => { activeIndex.current = index; }}
        noLimitInitializationSize={true}
        visible={true}
        noClose={true}
        noImgDetails={true}
        disableKeyboardSupport={true}
        changeable={false}
        downloadable={true}
        downloadInNewWindow={true}
        zoomSpeed={0.3}
        minScale={0.25}
        noNavbar={images.length < 2}
        scalable={false}
        images={images}
        customToolbar={tools => tools.map(tool => ({
          ...tool,
          // Keep the viewer's original action and bundled icon. A native button
          // also exposes the same action to Tab / Enter / Space and screen readers.
          render: (
            <button type="button" className="signal-viewer-tool"
              title={toolLabels[tool.key] || tool.key}
              aria-label={toolLabels[tool.key] || tool.key}>
              <i aria-hidden="true" className={`react-viewer-icon react-viewer-icon-${tool.key}`} />
            </button>
          ),
        }))}
      />
  );
}

export default ImageViewer
